/**
 * 表单项: 标签 + 控件 + 说明 / 错误。
 * children 是单个控件元素时, 自动补 id / aria-describedby / aria-invalid,
 * 标签的 htmlFor 自动指向它; 也可以显式传 htmlFor。
 */
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface FieldProps {
  label?: ReactNode;
  /** 灰色说明文字 (有 error 时被 error 取代) */
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  /** 标签右侧的附加内容 (如「忘记密码」链接) */
  labelAside?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, htmlFor, labelAside, className, children }: FieldProps) {
  const autoId = useId();
  const child = isValidElement(children) ? (children as ReactElement<Record<string, unknown>>) : null;
  const id = htmlFor ?? (child?.props.id as string | undefined) ?? `field-${autoId}`;
  const msgId = `${id}-msg`;
  const message = error || hint;

  const control = child
    ? cloneElement(child, {
        id,
        "aria-describedby": message ? msgId : undefined,
        ...(error ? { "aria-invalid": true } : null),
      })
    : children;

  return (
    <div className={cn("space-y-1.5", className)}>
      {(label || labelAside) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <label htmlFor={id} className="text-sm font-medium text-foreground">
              {label}
              {required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
            </label>
          )}
          {labelAside}
        </div>
      )}
      {control}
      {message && (
        <p id={msgId} className={cn("text-xs", error ? "text-danger-strong" : "text-muted-foreground")}>
          {message}
        </p>
      )}
    </div>
  );
}
